import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BILLING } from "@/lib/data";

const NAV_LINKS = [
    { label: "Bio", href: "#bio" },
    { label: "Reel", href: "#reel" },
    { label: "Acting", href: "#acting" },
    { label: "Directing", href: "#directing" },
    { label: "Gallery", href: "#gallery" },
    { label: "Profiles", href: "#profiles" },
    { label: "Contact", href: "#contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        const el = document.querySelector(href);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-300 ${
                scrolled
                    ? "bg-[#F7F5F0]/90 backdrop-blur border-b border-[#D1CFC9]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 md:py-5 flex items-center justify-between">
                <button
                    onClick={() => go("#top")}
                    data-testid="nav-home-link"
                    className="font-serif-display text-xl md:text-2xl tracking-tight text-[#1A1A1A] leading-none"
                >
                    Vickie Daignault
                    <span className="ml-2 hidden sm:inline-block align-middle">
                        <span className="inline-block w-1 h-1 bg-[#C67C4E] rounded-full" />
                    </span>
                </button>

                <nav className="hidden md:flex items-center gap-7 lg:gap-10 ml-auto md:pl-12 lg:pl-24">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.href}
                            onClick={() => go(l.href)}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A] link-underline"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <button
                    aria-label={open ? "Close menu" : "Open menu"}
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle"
                    className="md:hidden w-10 h-10 inline-flex items-center justify-center border border-[#1A1A1A] text-[#1A1A1A]"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        data-testid="mobile-menu"
                        className="md:hidden bg-[#F7F5F0] border-b border-[#D1CFC9]"
                    >
                        <div className="px-6 py-6 grid gap-4">
                            {NAV_LINKS.map((l) => (
                                <button
                                    key={l.href}
                                    onClick={() => go(l.href)}
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                    className="text-left text-base tracking-[0.18em] uppercase font-light text-[#1A1A1A]"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <div className="pt-4 mt-2 border-t border-[#D1CFC9] eyebrow">
                                {BILLING.union}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
